import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, MapPin, Mail, ChevronRight, ChevronDown, Heart, Users, Briefcase, GraduationCap, HandHeart, Sun } from "lucide-react";
import BridgeModelLogo from "@/components/site/BridgeModelLogo";
import SamGovBadge from "@/components/site/SamGovBadge";
import NationalFootprint from "@/components/site/NationalFootprint";
import BridgeModelStory from "@/components/site/BridgeModelStory";
import ChicagoHistory from "@/components/site/ChicagoHistory";
import FounderAuthority from "@/components/site/FounderAuthority";
import SizingCalculator from "@/components/site/SizingCalculator";
import { DeploymentProvider, nationalDeployment } from "@/lib/DeploymentContext";
import { parentBrand, deployments, expansionRoadmap } from "@/lib/deployments";

/* ---------------------------------------------------------------
 * HomePage · The Bridge Model (parent brand)
 *
 * Warm cream/sage/gold palette. NOT the navy civic dossier — that
 * lives at /baytown. This page introduces the program to any visitor
 * (families, partner cities, funders, press) and hands them off to
 * the formal Baytown deployment when they're ready to go deeper.
 * --------------------------------------------------------------- */

// Curated free-use community imagery (Unsplash · royalty-free).
// Each chosen for warm, hopeful, multi-ethnic, multi-generational tone.
const heroImages = [
  {
    src: "https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=900&q=80",
    alt: "Multigenerational family laughing together outdoors",
    label: "Family",
  },
  {
    src: "https://images.unsplash.com/photo-1593113598332-cd288d649433?auto=format&fit=crop&w=900&q=80",
    alt: "Community members raising hands together in worship and service",
    label: "Faith",
  },
  {
    src: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=900&q=80",
    alt: "Diverse team collaborating around a table at work",
    label: "Work",
  },
];

const valuesPillars = [
  {
    icon: Heart,
    title: "Family",
    body: "We assess and serve the whole household in a single intake — children, parents, and aging grandparents under one roof, on one day.",
  },
  {
    icon: HandHeart,
    title: "Faith",
    body: "Rooted in the tradition of faith-based community service. We honor that heritage and serve every household equally, regardless of belief.",
  },
  {
    icon: Briefcase,
    title: "Work",
    body: "Direct pipelines to living-wage trade careers — HVAC, welding, process technology, allied health — paired with childcare and transportation support.",
  },
  {
    icon: GraduationCap,
    title: "Education",
    body: "Lee College certificates, GCCISD parent-engagement, after-school enrollment. Education is the long bridge from stability to generational wealth.",
  },
  {
    icon: Users,
    title: "Community",
    body: "Block-level outreach, partner-network case management, multigenerational picnics. Real relationships, sustained over years.",
  },
  {
    icon: Sun,
    title: "Health & Dignity",
    body: "Senior aging-in-place. Harris Health primary-care continuity. Home modifications. The right to grow old in your own home.",
  },
];

const communityGallery = [
  {
    src: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=800&q=80",
    label: "Diverse community gathering",
    tag: "Community",
  },
  {
    src: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=800&q=80",
    label: "Diverse team collaboration",
    tag: "Workforce",
  },
  {
    src: "https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?auto=format&fit=crop&w=800&q=80",
    label: "Senior wellness & dignity in aging",
    tag: "Senior Care",
  },
  {
    src: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&w=800&q=80",
    label: "Multigenerational family at a picnic",
    tag: "Family",
  },
  {
    src: "https://images.unsplash.com/photo-1606127195229-bce06fae3489?auto=format&fit=crop&w=800&q=80",
    label: "Skilled trade apprentice on the job",
    tag: "Trade Pipeline",
  },
  {
    src: "https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=800&q=80",
    label: "Healthcare worker with patient",
    tag: "Health",
  },
];

export default function HomePage() {
  const [deployOpen, setDeployOpen] = useState(false);
  const deployRef = useRef(null);

  useEffect(() => {
    if (!deployOpen) return;
    const onDocClick = (e) => {
      if (deployRef.current && !deployRef.current.contains(e.target)) {
        setDeployOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setDeployOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [deployOpen]);

  return (
    <DeploymentProvider value={nationalDeployment}>
    <main
      data-testid="home-page"
      className="bg-[#FDF8EF] text-[#1F2937] min-h-screen"
    >
      {/* ============================ NAV (light, minimal) ============================ */}
      <header
        data-testid="home-nav"
        className="sticky top-0 z-50 bg-[#FDF8EF]/90 backdrop-blur border-b border-[#E8DCC3]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="home-nav-logo" className="flex items-center gap-3 group">
            <BridgeModelLogo
              size={40}
              primary="#1F2937"
              accent="#B8860B"
              sunGlow="#F4D77A"
            />
            <span className="font-display text-lg leading-none text-[#1F2937]">
              The <span className="italic">Bridge</span> Model
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#promise" className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563] hover:text-[#B8860B]">Promise</a>
            <a href="#national" className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563] hover:text-[#B8860B]">National</a>
            <a href="#about" className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563] hover:text-[#B8860B]">About</a>
            <a href="#founder" className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563] hover:text-[#B8860B]">Founder</a>
            <a href="#urban-rural" className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563] hover:text-[#B8860B]">Urban+Rural</a>

            {/* Deployments dropdown */}
            <div
              className="relative"
              ref={deployRef}
              data-testid="nav-deployments-wrapper"
            >
              <button
                type="button"
                data-testid="nav-deployments-trigger"
                onClick={() => setDeployOpen((v) => !v)}
                aria-expanded={deployOpen}
                aria-haspopup="menu"
                className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-[#4B5563] hover:text-[#B8860B]"
              >
                Proposals <ChevronDown className={`h-3 w-3 transition-transform ${deployOpen ? "rotate-180" : ""}`} />
              </button>
              {deployOpen && (
                <div
                  data-testid="nav-deployments-menu"
                  role="menu"
                  className="absolute right-0 top-full mt-2 w-72 bg-white border border-[#E8DCC3] shadow-xl z-50"
                >
                  <Link
                    to="/baytown"
                    data-testid="nav-deployment-baytown"
                    onClick={() => setDeployOpen(false)}
                    className="block px-4 py-3 border-b border-[#F3E9D2] hover:bg-[#FDF8EF] transition-colors"
                  >
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#B8860B] mb-1">
                      Proposal 01 · Pending Council
                    </div>
                    <div className="font-display text-base text-[#1F2937] flex items-center justify-between">
                      Baytown, TX
                      <ArrowRight className="h-3.5 w-3.5 text-[#B8860B]" />
                    </div>
                    <div className="text-[11px] text-[#6B7280] mt-1">Harris County · CDBG · Lee College pipeline</div>
                  </Link>
                  <Link
                    to="/north-houston"
                    data-testid="nav-deployment-north-houston"
                    onClick={() => setDeployOpen(false)}
                    className="block px-4 py-3 border-b border-[#F3E9D2] hover:bg-[#FDF8EF] transition-colors"
                  >
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#B8860B] mb-1">
                      Proposal 02 · In Preparation
                    </div>
                    <div className="font-display text-base text-[#1F2937] flex items-center justify-between">
                      North Houston District, TX
                      <ArrowRight className="h-3.5 w-3.5 text-[#B8860B]" />
                    </div>
                    <div className="text-[11px] text-[#6B7280] mt-1">Formerly Greenspoint · IAH logistics corridor</div>
                  </Link>
                  <Link
                    to="/compare"
                    data-testid="nav-compare-link"
                    onClick={() => setDeployOpen(false)}
                    className="block px-4 py-3 border-b border-[#F3E9D2] bg-[#FBF6E5] hover:bg-[#F5EBC8] transition-colors"
                  >
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#B8860B] mb-1">
                      Side-by-Side
                    </div>
                    <div className="font-display text-base text-[#1F2937] flex items-center justify-between">
                      Compare Both Proposals
                      <ArrowRight className="h-3.5 w-3.5 text-[#B8860B]" />
                    </div>
                    <div className="text-[11px] text-[#6B7280] mt-1">Budget · workforce · outcomes · regional case</div>
                  </Link>
                  <a
                    href="#deployments"
                    data-testid="nav-deployment-roadmap"
                    onClick={() => setDeployOpen(false)}
                    className="block px-4 py-3 hover:bg-[#FDF8EF] transition-colors"
                  >
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#B8860B] mb-1">
                      Tier 02 · Discovery
                    </div>
                    <div className="font-display text-base text-[#1F2937] flex items-center justify-between">
                      Expansion Roadmap
                      <ChevronRight className="h-3.5 w-3.5 text-[#B8860B]" />
                    </div>
                    <div className="text-[11px] text-[#6B7280] mt-1">La Porte · Pasadena · Texas City · Gulf Coast</div>
                  </a>
                </div>
              )}
            </div>

            <a href="#sizing" className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563] hover:text-[#B8860B]">Sizing</a>
            <a href="#contact" className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563] hover:text-[#B8860B]">Apply</a>
          </nav>
          <a
            href="#contact"
            data-testid="home-nav-schedule-cta"
            className="inline-flex items-center gap-2 bg-[#1F2937] hover:bg-[#0A192F] text-[#FDF8EF] px-4 h-10 font-mono text-[10px] uppercase tracking-widest transition-colors"
          >
            Schedule a Briefing <ArrowRight className="h-3 w-3" />
          </a>
        </div>
      </header>

      {/* ============================ HERO ============================ */}
      <section data-testid="home-hero" className="relative overflow-hidden">
        {/* Warm sun-wash background */}
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FDF8EF] via-[#F8EBD0] to-[#EFE3C7]" />
          <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-[#F4D77A]/40 blur-3xl" />
          <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#A3B89B]/30 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-[#B8860B]/10 text-[#7C5A0B] px-3 py-1.5 mb-6 font-mono text-[10px] uppercase tracking-widest">
                <Sparkles className="h-3 w-3" /> A national service-connector methodology · urban &amp; rural
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-[#1F2937] leading-[1.05] mb-6">
                The jobs are there.<br />
                The resources are there.<br />
                <em className="italic text-[#B8860B]">We connect the dots.</em>
              </h1>
              <p className="text-lg lg:text-xl text-[#4B5563] leading-relaxed max-w-2xl mb-8">
                From inner-city neighborhoods to rural counties, every
                underserved community in America already has the pieces —
                colleges, hospitals, housing authorities, employers. What is
                missing is the human chain that walks families to them.
                <strong className="text-[#1F2937]">
                  {" "}The Bridge Model is that chain.
                </strong>
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/baytown"
                  data-testid="hero-cta-baytown"
                  className="inline-flex items-center gap-2 bg-[#1F2937] hover:bg-[#0A192F] text-[#FDF8EF] px-7 h-12 font-mono text-xs uppercase tracking-widest transition-colors"
                >
                  See Proposal 01 · Baytown <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="#sizing"
                  data-testid="hero-cta-bring"
                  className="inline-flex items-center gap-2 border border-[#1F2937] text-[#1F2937] hover:bg-[#1F2937] hover:text-[#FDF8EF] px-7 h-12 font-mono text-xs uppercase tracking-widest transition-colors"
                >
                  Bring Bridge to Your Community
                </a>
              </div>
              <div className="mt-6">
                <SamGovBadge variant="light" data-testid="hero-sam-badge" />
              </div>
            </div>

            {/* Right: image collage */}
            <div className="lg:col-span-5 relative">
              <div className="grid grid-cols-6 gap-3 lg:gap-4">
                <div className="col-span-4 aspect-[4/5] overflow-hidden bg-[#A3B89B]/20 shadow-xl">
                  <img
                    src={heroImages[0].src}
                    alt={heroImages[0].alt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="col-span-2 space-y-3 lg:space-y-4">
                  <div className="aspect-square overflow-hidden bg-[#F4D77A]/30 shadow-lg">
                    <img
                      src={heroImages[1].src}
                      alt={heroImages[1].alt}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-square overflow-hidden bg-[#F4D77A]/30 shadow-lg">
                    <img
                      src={heroImages[2].src}
                      alt={heroImages[2].alt}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ HUMAN PROMISE BAND ============================ */}
      <section
        id="promise"
        data-testid="home-promise"
        className="relative py-20 lg:py-24 bg-gradient-to-b from-[#F8EBD0] to-[#FDF8EF] overflow-hidden"
      >
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full bg-[#F4D77A]/25 blur-3xl" />
        </div>
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#B8860B] mb-5">
            The Promise to Every Household
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-[#1F2937] leading-[1.08] mb-7">
            Stronger communities.<br />
            <em className="italic text-[#B8860B]">One trusted hand.</em>
          </h2>
          <p className="text-base lg:text-lg xl:text-xl text-[#4B5563] leading-relaxed max-w-3xl mx-auto">
            The Bridge Model walks underserved households through every door
            they need to open — work, school, healthcare, housing, senior
            care — with{" "}
            <strong className="text-[#1F2937]">
              one trained Service Connector by their side for the long haul.
            </strong>{" "}
            Faith, family, and dignity, in service of every household we meet.
          </p>
          <div className="mt-10 flex items-center justify-center gap-3">
            <span className="h-px w-12 bg-[#B8860B]/60" />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#B8860B]">
              Every door · Every household · For the long haul
            </span>
            <span className="h-px w-12 bg-[#B8860B]/60" />
          </div>
        </div>
      </section>

      {/* ============================ NATIONAL FOOTPRINT ============================ */}
      <NationalFootprint />

      {/* ============================ ABOUT / TAGLINE ============================ */}
      <section
        id="about"
        data-testid="home-about"
        className="bg-[#FDF8EF] py-20 lg:py-28 border-y border-[#E8DCC3]"
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#B8860B] mb-4">
            About · The Bridge Model
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2937] leading-tight mb-6">
            A 65-year-old tradition.<br />
            <em className="italic text-[#B8860B]">A new generation of practitioners.</em>
          </h2>
          <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed max-w-3xl mx-auto">
            Born in Chicago&apos;s Woodlawn neighborhood in 1960 and trained inside
            The Woodlawn Organization / WCDC under the late Dr. Leon Finney Jr.,
            The Bridge Model has been refined over six decades into a
            municipally-deployable, fidelity-audited service-connector
            framework. Today it is offered to American communities as a
            licensed methodology — one Hub, three competitively-selected
            agencies, household counts and budgets sized to the host
            community, and every Service Connector held to a 1:38 caseload cap.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a
              href="#story"
              className="font-mono text-[11px] uppercase tracking-widest text-[#B8860B] hover:text-[#1F2937] inline-flex items-center gap-2"
            >
              Read the full Story <ChevronRight className="h-3 w-3" />
            </a>
          </div>
        </div>
      </section>

      {/* ============================ THE BRIDGE MODEL STORY (full) ============================ */}
      <BridgeModelStory />

      {/* ============================ CHICAGO LINEAGE ============================ */}
      <ChicagoHistory />

      {/* ============================ FULL FOUNDER AUTHORITY ============================ */}
      <div id="founder">
        <FounderAuthority />
      </div>

      {/* ============================ VALUES PILLARS ============================ */}
      <section
        id="values"
        data-testid="home-values"
        className="py-20 lg:py-28 bg-gradient-to-b from-[#FDF8EF] to-[#F4EBD8]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#B8860B] mb-3">
              What We Stand For
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2937] leading-tight">
              Six pillars. <em className="italic text-[#B8860B]">One household at a time.</em>
            </h2>
          </div>

          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {valuesPillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <li
                  key={p.title}
                  data-testid={`values-pillar-${i}`}
                  className="bg-white p-7 lg:p-8 shadow-sm hover:shadow-md transition-shadow border border-[#E8DCC3]"
                >
                  <div className="inline-flex h-12 w-12 items-center justify-center bg-[#F4D77A]/30 mb-5">
                    <Icon className="h-6 w-6 text-[#B8860B]" />
                  </div>
                  <h3 className="font-display text-xl text-[#1F2937] mb-2">{p.title}</h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{p.body}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ============================ COMMUNITY GALLERY ============================ */}
      <section
        id="community"
        data-testid="home-community"
        className="py-20 lg:py-28 bg-[#FDF8EF]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-12 items-baseline">
            <div className="lg:col-span-7">
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#B8860B] mb-3">
                The Communities We Serve
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2937] leading-tight">
                The lives behind <em className="italic text-[#B8860B]">every number.</em>
              </h2>
            </div>
            <div className="lg:col-span-5 text-[#4B5563] leading-relaxed">
              <p>
                We serve every household regardless of background, language,
                immigration status, faith, or family structure. From the
                multigenerational kitchen table to the trade-school classroom,
                Bridge meets families where they live.
              </p>
            </div>
          </div>

          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {communityGallery.map((g, i) => (
              <li
                key={g.label}
                data-testid={`community-gallery-${i}`}
                className="group relative aspect-[4/5] overflow-hidden shadow-md"
              >
                <img
                  src={g.src}
                  alt={g.label}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#F4D77A] mb-1">{g.tag}</div>
                  <div className="font-display text-base text-white leading-snug">{g.label}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============================ DEPLOYMENTS ROADMAP ============================ */}
      <section
        id="deployments"
        data-testid="home-deployments"
        className="py-20 lg:py-28 bg-gradient-to-br from-[#F4EBD8] via-[#F8EBD0] to-[#EFE3C7]"
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#B8860B] mb-3">
              Where the Model Goes Next
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2937] leading-tight">
              Community by community. <em className="italic text-[#B8860B]">Family by family.</em>
            </h2>
            <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed mt-6 max-w-2xl mx-auto">
              Each deployment is a five-year contract with a host city or district, three
              competitively-selected agencies, and an independent annual evaluation. <strong>
              Household counts, budgets, and connector staffing are sized to the
              community</strong> — a 60,000-resident district and a 200,000-resident
              city do not get the same plan.
            </p>
          </div>

          {/* Featured: Baytown */}
          <div className="mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#8B0000] mb-2">
              Tier 01 · Active Proposals
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-5 mb-8">
            <Link
              to="/baytown"
              data-testid="home-deployment-baytown"
              className="block bg-white border-2 border-[#1F2937] p-6 lg:p-7 shadow-md hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#B8860B] mb-2">
                <MapPin className="h-3 w-3" /> Proposal 01 · Pending Council
              </div>
              <h3 className="font-display text-xl lg:text-2xl text-[#1F2937] mb-2">
                The Baytown Bridge
              </h3>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
                Harris County, Texas · 1,500 households · 5-year CDBG-anchored
                program · 3-agency Tri-Portfolio · full proposal, budget,
                implementation, federal alignment.
              </p>
              <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#1F2937]">
                View the proposal <ArrowRight className="h-3 w-3" />
              </span>
            </Link>

            <Link
              to="/north-houston"
              data-testid="home-deployment-nhd"
              className="block bg-white border-2 border-[#B8860B] p-6 lg:p-7 shadow-md hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#B8860B] mb-2">
                <MapPin className="h-3 w-3" /> Proposal 02 · In Preparation
              </div>
              <h3 className="font-display text-xl lg:text-2xl text-[#1F2937] mb-2">
                North Houston District
              </h3>
              <p className="text-sm text-[#4B5563] leading-relaxed mb-4">
                Formerly Greenspoint · The diversified workforce hub. Aviation
                (IAH), allied health, logistics, hospitality, professional
                services, and light manufacturing — with anti-displacement
                protections for long-time residents.
              </p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#1F2937]">
                  View the dossier <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            </Link>
          </div>

          {/* Roadmap upcoming */}
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#B8860B] mb-3 mt-12">
            Tier 02 · Discovery · Gulf Coast TX
          </div>
          <ul data-testid="home-roadmap" className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {expansionRoadmap.slice(1).map((r) => (
              <li
                key={r.city}
                className="bg-white/70 backdrop-blur p-5 border border-[#E8DCC3]"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#B8860B] mb-2">
                  {r.period}
                </div>
                <div className="font-display text-base text-[#1F2937] mb-1">{r.city}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#6B7280]">{r.phase}</div>
              </li>
            ))}
          </ul>

          {/* Tier 03 · Open to applications */}
          <div data-testid="tier-03-open" className="bg-[#1F2937] text-[#FDF8EF] p-7 lg:p-9">
            <div className="grid lg:grid-cols-12 gap-6 items-center">
              <div className="lg:col-span-9">
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#F4D77A] mb-2">
                  Tier 03 · Open to Applications · Nationwide
                </div>
                <div className="font-display text-xl lg:text-2xl text-[#FDF8EF] mb-2">
                  Any underserved community in the United States.
                </div>
                <p className="text-sm text-[#D1D5DB] leading-relaxed">
                  Urban CDBG entitlement city. Rural persistent-poverty county.
                  Tribal nation. Foundation-funded pilot. If the resources exist
                  and the chain does not, this is for you.
                </p>
              </div>
              <div className="lg:col-span-3 lg:text-right">
                <a
                  href="#sizing"
                  data-testid="tier-03-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#F4D77A] hover:bg-[#FBEAA0] text-[#1F2937] h-11 px-5 font-mono text-[10px] uppercase tracking-widest transition-colors"
                >
                  Apply <ArrowRight className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ SIZING CALCULATOR ============================ */}
      <SizingCalculator />

      {/* ============================ CONTACT ============================ */}
      <section
        id="contact"
        data-testid="home-contact"
        className="py-20 lg:py-28 bg-[#FDF8EF] border-t border-[#E8DCC3]"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#B8860B] mb-3">
            License The Bridge Model
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2937] leading-tight mb-6">
            Bring the model to <em className="italic text-[#B8860B]">your community.</em>
          </h2>
          <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed mb-10 max-w-2xl mx-auto">
            Municipal leaders, foundation officers, and partner organizations — reach
            out for a discovery briefing on what a Bridge Model deployment looks
            like in your city.
          </p>
          <a
            href={`mailto:${parentBrand.contactEmail}?subject=Bridge%20Model%20Discovery%20Briefing`}
            data-testid="home-contact-email"
            className="inline-flex items-center gap-3 bg-[#B8860B] hover:bg-[#9E7409] text-[#FDF8EF] px-8 h-14 font-mono text-sm uppercase tracking-widest transition-colors"
          >
            <Mail className="h-4 w-4" /> {parentBrand.contactEmail}
          </a>
        </div>
      </section>

      {/* ============================ FOOTER ============================ */}
      <footer
        data-testid="home-footer"
        className="bg-[#1F2937] text-[#FDF8EF] py-14"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-8 items-start">
          <div className="flex items-center gap-3">
            <BridgeModelLogo size={48} primary="#FDF8EF" accent="#F4D77A" sunGlow="#F4D77A" />
            <div>
              <div className="font-display text-lg leading-none">The <span className="italic">Bridge</span> Model</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F4D77A] mt-1">{parentBrand.domain}</div>
            </div>
          </div>
          <div className="text-sm text-[#D1D5DB] leading-relaxed">
            Designed and licensed by Sterile Mate LLC. First proposal submitted to
            Baytown, Texas. Future deployments along the Gulf Coast LMI corridor
            through 2028.
          </div>
          <div className="text-right md:text-right">
            <Link
              to="/baytown"
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#F4D77A] hover:text-white"
            >
              View Baytown Proposal <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 pt-6 border-t border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#F4D77A] leading-relaxed mb-3">
            The Bridge Model serves underserved households of every ethnic and social background &mdash; without prejudice, without precondition. Title VI · Fair Housing · ADA · Section 504 compliant.
          </p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#9CA3AF] leading-relaxed">
            © {new Date().getFullYear()} {parentBrand.consultancy} · All rights reserved · The Bridge Model methodology (Hub-and-Spokes architecture, Service Connector caseload structure, three-portfolio framework, train-the-trainer curriculum, intake matrices, financial-forecast templates, and dossier content) is the proprietary work product of {parentBrand.proprietor}, protected under U.S. copyright law, the Texas Uniform Trade Secrets Act, and the federal Defend Trade Secrets Act. Unauthorized reproduction, distribution, or replication is strictly prohibited. Proposal access governed by a click-wrap reviewer acknowledgment · all acknowledgments logged for audit.
          </p>
        </div>
      </footer>
    </main>
    </DeploymentProvider>
  );
}
